import { infiniteQueryOptions, queryOptions, useInfiniteQuery, useMutation, useQuery, useQueryClient, type InfiniteData, type QueryClient } from '@tanstack/react-query'
import { api, unwrap } from '@/lib/api'
import type { Campaign, CampaignStatus, DataEnvelope, Paginated } from '@/types/api'

export interface CampaignListParams {
  search?: string
  status?: CampaignStatus
}

export const campaignKeys = {
  all: ['campaigns'] as const,
  lists: () => [...campaignKeys.all, 'list'] as const,
  list: (params: CampaignListParams) => [...campaignKeys.lists(), params] as const,
  options: () => [...campaignKeys.lists(), 'options'] as const,
  details: () => [...campaignKeys.all, 'detail'] as const,
  detail: (id: string) => [...campaignKeys.details(), id] as const,
}

/* ------------------------------------------------------------------ queries */

export function campaignsInfiniteOptions(params: CampaignListParams) {
  return infiniteQueryOptions({
    queryKey: campaignKeys.list(params),
    queryFn: ({ pageParam, signal }) =>
      api.get<Paginated<Campaign>>('/campaigns', { signal, query: { search: params.search, status: params.status, cursor: pageParam, per_page: 50 } }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (last) => last.meta?.next_cursor ?? undefined,
  })
}

export function useCampaigns(params: CampaignListParams) {
  return useInfiniteQuery(campaignsInfiniteOptions(params))
}

/** Up to 100 campaigns — for pickers, filters and colour lookups. */
export function useCampaignOptions(enabled = true) {
  return useQuery({
    queryKey: campaignKeys.options(),
    queryFn: ({ signal }) => api.get<Paginated<Campaign>>('/campaigns', { signal, query: { per_page: 100 } }),
    select: (page) => page.data,
    enabled,
    staleTime: 30_000,
  })
}

export function campaignOptions(id: string) {
  return queryOptions({
    queryKey: campaignKeys.detail(id),
    queryFn: ({ signal }) => unwrap(api.get<DataEnvelope<Campaign>>(`/campaigns/${id}`, { signal })),
  })
}

export function useCampaign(id: string) {
  return useQuery(campaignOptions(id))
}

/* ---------------------------------------------------------------- caching */

/** Campaign dates / status clip schedules: refresh everything schedule-derived. */
export function invalidateScheduling(qc: QueryClient) {
  void qc.invalidateQueries({ queryKey: ['schedules'] })
  void qc.invalidateQueries({ queryKey: ['calendar'] })
  void qc.invalidateQueries({ queryKey: ['displays', 'detail'] })
  void qc.invalidateQueries({ queryKey: ['dashboard'] })
}

function patchCampaignCache(qc: QueryClient, campaign: Campaign) {
  qc.setQueryData(campaignKeys.detail(campaign.id), campaign)
  qc.setQueriesData<InfiniteData<Paginated<Campaign>>>({ queryKey: campaignKeys.lists() }, (data) =>
    data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.map((c) => (c.id === campaign.id ? campaign : c)) })) } : data,
  )
}

/* ---------------------------------------------------------------- mutations */

export interface CampaignInput {
  name: string
  code: string | null
  advertiser: string | null
  description: string | null
  start_date: string | null
  end_date: string | null
  priority: number
  status: CampaignStatus
  color: string | null
  /** Signetix Logic: library rule that must hold (null = unconditional). */
  condition_rule_id?: string | null
  condition_unknown_as?: boolean
}

export function useCreateCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: CampaignInput) => unwrap(api.post<DataEnvelope<Campaign>>('/campaigns', input)),
    onSuccess: (c) => {
      qc.setQueryData(campaignKeys.detail(c.id), c)
      void qc.invalidateQueries({ queryKey: campaignKeys.lists() })
      void qc.invalidateQueries({ queryKey: ['dashboard'] })
    },
  })
}

export function useUpdateCampaign(id: string) {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (input: Partial<CampaignInput>) => unwrap(api.patch<DataEnvelope<Campaign>>(`/campaigns/${id}`, input)),
    onSuccess: (c) => {
      patchCampaignCache(qc, c)
      void qc.invalidateQueries({ queryKey: campaignKeys.lists() })
      // Schedules embed the campaign's name / colour / status.
      invalidateScheduling(qc)
    },
  })
}

export function useDeleteCampaign() {
  const qc = useQueryClient()
  return useMutation({
    mutationFn: (id: string) => api.delete(`/campaigns/${id}`),
    onSuccess: (_d, id) => {
      qc.setQueriesData<InfiniteData<Paginated<Campaign>>>({ queryKey: campaignKeys.lists() }, (data) =>
        data?.pages ? { ...data, pages: data.pages.map((p) => ({ ...p, data: p.data.filter((c) => c.id !== id) })) } : data,
      )
      qc.removeQueries({ queryKey: campaignKeys.detail(id) })
      void qc.invalidateQueries({ queryKey: campaignKeys.lists() })
      // Its schedules are deleted with it.
      invalidateScheduling(qc)
    },
  })
}
